import { useEffect, useRef } from "react";
import { Card, Col, Form, Placeholder, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, useBlocker } from "react-router-dom";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import { useKit } from "hooks/useKits";
import BotaoSalvarKit from "components/kit/BotaoSalvarKit";
import ComponentesKitCard from "components/kit/ComponentesKitCard";

// ─── Tipos exportados para os componentes filhos ────────────────────────────────

export interface KitComponenteFormValue {
  componenteProdutoId: string;
  produtoCodigo: number;
  produtoDescricao: string;
  quantidade: number;
  precoNoKit: number;
  custoUnitario: number;
  vendaUnitario: number;
}

export interface KitFormValues {
  descricao: string;
  precoKit: number;
  inativo: boolean;
  componentes: KitComponenteFormValue[];
}

// ─── Breadcrumb ────────────────────────────────────────────────────────────────

const buildBreadcrumb = (isEdit: boolean) => [
  { label: "Cadastros" },
  { label: "Kits de Produtos", url: "/cadastros/kits" },
  { label: isEdit ? "Editar" : "Novo", active: true },
];

// ─── Formulário ────────────────────────────────────────────────────────────────

const KitForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: detalhe, isLoading } = useKit(id ?? "", isEdit);

  const form = useForm<KitFormValues>({
    defaultValues: {
      descricao: "",
      precoKit: 0,
      inativo: false,
      componentes: [],
    },
  });

  const { register, reset, formState: { errors, isDirty } } = form;

  // Ref para liberar a navegação logo após salvar (o reset não limpa isDirty
  // a tempo para o closure do blocker — então sinalizamos via ref).
  const bypassBlocker = useRef(false);

  // Bloqueia a navegação se houver alterações não salvas
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !bypassBlocker.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Carrega os dados do detalhe no formulário ao editar
  useEffect(() => {
    if (detalhe) {
      reset({
        descricao: detalhe.descricao,
        precoKit: detalhe.precoKit,
        inativo: detalhe.inativo,
        componentes: detalhe.componentes.map((c) => ({
          componenteProdutoId: c.componenteProdutoId,
          produtoCodigo: c.codigo,
          produtoDescricao: c.descricao,
          quantidade: c.quantidade,
          precoNoKit: c.precoNoKit,
          custoUnitario: c.custoUnitario,
          vendaUnitario: c.vendaUnitario,
        })),
      });
    }
  }, [detalhe, reset]);

  if (isEdit && isLoading) {
    return (
      <div>
        <PageBreadcrumb items={buildBreadcrumb(true)} />
        <div className="mb-9">
          <div className="mb-4">
            <Placeholder animation="wave" as="h2" className="mb-1">
              <Placeholder xs={4} />
            </Placeholder>
            <Placeholder animation="wave" as="p" className="mb-0">
              <Placeholder xs={6} />
            </Placeholder>
          </div>

          <Card className="mb-4">
            <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
              <Placeholder animation="wave" as="h5" className="mb-0">
                <Placeholder xs={3} style={{ width: 140 }} />
              </Placeholder>
              <Placeholder.Button variant="secondary" xs={1} style={{ width: 60 }} />
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Placeholder animation="wave" as="div" className="mb-1"><Placeholder xs={2} /></Placeholder>
                  <Placeholder animation="wave" as="div"><Placeholder xs={12} style={{ height: 38 }} /></Placeholder>
                </Col>
                <Col xs={12} md={6}>
                  <Placeholder animation="wave" as="div" className="mb-1"><Placeholder xs={3} /></Placeholder>
                  <Placeholder animation="wave" as="div"><Placeholder xs={12} style={{ height: 38 }} /></Placeholder>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={buildBreadcrumb(isEdit)} />

      <FormProvider {...form}>
        <UnsavedChangesModal
          show={blocker.state === "blocked"}
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />

        <div className="mb-9">
          <div className="mb-4">
            <h2 className="mb-1">
              {isEdit ? "Editar Kit de Produtos" : "Novo Kit de Produtos"}
            </h2>
            <p className="text-body-secondary mb-0">
              Preencha as informações do kit de produtos
            </p>
          </div>

          {/* Card principal */}
          <Card className="mb-4">
            <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                Kit de Produtos
                {isEdit && detalhe && (
                  <span className="badge bg-primary-subtle text-primary fs-10 ms-2 align-middle">
                    #{detalhe.codigo}
                  </span>
                )}
              </h5>
              <Form.Check
                type="switch"
                id="inativo"
                label="Inativo"
                className="mb-0"
                {...register("inativo")}
              />
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {/* Descrição */}
                <Col xs={12} md={8}>
                  <Form.Group>
                    <Form.Label>
                      Descrição <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      {...register("descricao", { required: "A descrição é obrigatória" })}
                      placeholder="Ex: Kit Shampoo + Condicionador..."
                      isInvalid={!!errors.descricao}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.descricao?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Preço do Kit */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>
                      Preço do Kit (R$) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      {...register("precoKit", {
                        required: "O preço do kit é obrigatório",
                        min: { value: 0, message: "O preço deve ser positivo" },
                        valueAsNumber: true,
                      })}
                      onFocus={(e) => e.currentTarget.select()}
                      isInvalid={!!errors.precoKit}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.precoKit?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Toggle v2 — desabilitado com tooltip */}
                <Col xs={12}>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="opcaoEscolha"
                      label="Item com opção de escolha"
                      disabled
                    />
                    <span
                      className="badge bg-secondary-subtle text-body-secondary"
                      title="Esta funcionalidade estará disponível em breve"
                      style={{ cursor: "help" }}
                    >
                      em breve
                    </span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Card de componentes */}
          <ComponentesKitCard />

          {/* Rodapé com botão salvar */}
          <div className="d-flex justify-content-end mt-4">
            <BotaoSalvarKit id={id} bypassBlockerRef={bypassBlocker} />
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default KitForm;
